import { FormEvent, useEffect, useState } from "react";

import { PredictionPipeline } from "generated/hooks";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Spinner } from "~/components/ui/spinner";
import { Switch } from "~/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";

export interface QueuePredictionDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (
    pipeline: PredictionPipeline,
    refreshCache: boolean
  ) => Promise<void>;
  error?: string;
  loading?: boolean;
}

export const QueuePredictionDialog = ({
  isOpen,
  onCancel,
  onConfirm,
  error,
  loading,
}: QueuePredictionDialogProps) => {
  const [pipeline, setPipeline] = useState(PredictionPipeline.New);
  const [refreshCache, setRefreshCache] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setPipeline(PredictionPipeline.New);
      setRefreshCache(true);
    }
  }, [isOpen]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onConfirm(pipeline, refreshCache);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Queue Prediction</DialogTitle>
            <DialogDescription>
              Choose which prediction pipeline to run for this test.
            </DialogDescription>
          </DialogHeader>

          <RadioGroup
            value={pipeline}
            onValueChange={(value) => setPipeline(value as PredictionPipeline)}
            className="gap-3"
            disabled={loading}
          >
            <Label
              htmlFor="queue-prediction-new"
              className="flex cursor-pointer items-start gap-3 rounded-md border p-4"
            >
              <RadioGroupItem
                id="queue-prediction-new"
                value={PredictionPipeline.New}
                className="mt-0.5"
              />
              <span>
                <span className="block font-medium">New pipeline</span>
                <span className="block text-sm font-normal text-muted-foreground">
                  Extract the new feature set and run the configured model.
                </span>
              </span>
            </Label>
            <Label
              htmlFor="queue-prediction-old"
              className="flex cursor-pointer items-start gap-3 rounded-md border p-4"
            >
              <RadioGroupItem
                id="queue-prediction-old"
                value={PredictionPipeline.Old}
                className="mt-0.5"
              />
              <span>
                <span className="block font-medium">Old pipeline</span>
                <span className="block text-sm font-normal text-muted-foreground">
                  Use the legacy feature path with nn_attention_v2.
                </span>
              </span>
            </Label>
            <Label
              htmlFor="queue-prediction-both"
              className="flex cursor-pointer items-start gap-3 rounded-md border p-4"
            >
              <RadioGroupItem
                id="queue-prediction-both"
                value={PredictionPipeline.Both}
                className="mt-0.5"
              />
              <span>
                <span className="block font-medium">Both pipelines</span>
                <span className="block text-sm font-normal text-muted-foreground">
                  Queue both models so their results can be compared.
                </span>
              </span>
            </Label>
          </RadioGroup>

          <div className="flex items-center justify-between rounded-md border p-4">
            <Label htmlFor="queue-prediction-refresh-cache">
              Recalculate features? (~3 min)
            </Label>
            <Switch
              id="queue-prediction-refresh-cache"
              checked={refreshCache}
              onCheckedChange={(checked) => setRefreshCache(Boolean(checked))}
              disabled={loading}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Spinner className="mr-2" /> : null}
              Queue prediction
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
